import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { api } from '@api'
import { createQuery } from '@tanstack/solid-query';
import { type Component, Show } from 'solid-js'
import { css } from 'styled-system/css'
import { Box } from 'styled-system/jsx'

type ImageCardProps = {
  image: {
    title: string
    src?: string
  }
  onClick?: () => void
  objectFit?: 'cover' | 'contain' | 'fill'
}

export const Image: Component<ImageCardProps> = (props) => {
  const imageQuery = createQuery(() => ({
    queryKey: ['setup-image-single', props.image.src],
    queryFn: async () => {
      const src = props.image.src!

      if (src.startsWith('http')) {
        return src
      }

      const { data, error } = await api().storage
        .from('setup-images')
        .createSignedUrl(src, 3600)

      if (error || !data) {
        throw new Error(`Failed to load ${props.image.title}: ${error?.message}`)
      }

      return data.signedUrl
    },
    enabled: !!props.image.src,
    staleTime: 1000 * 60 * 50,
    gcTime: 1000 * 60 * 60,
  }))

  return (
    <Box
      onClick={props.onClick}
      class={css({
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        cursor: props.onClick ? 'pointer' : 'default',
        display: 'flex',
      })}
    >
      <Show when={imageQuery.data} fallback={
        <Skeleton
          class={css({
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 0,
          })} />
      }>
        <img
          src={imageQuery.data}
          alt={props.image.title}
          style={{
            'object-fit': props.objectFit ?? 'cover',
          }}
          class={css({
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 0,
          })}
        />
      </Show>

      <Badge
        variant="subtle"
        size="sm"
        class={css({
          position: 'absolute',
          bottom: 2,
          left: 2,
          zIndex: 1,
        })}
      >
        {props.image.title}
      </Badge>
    </Box>
  )
}
